import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import { isValidString } from '../common/CommonUtilsMethods';
import {
    buildWorkyAuthPathWithEmail,
    readWorkyAuthEmailFromLocationSearch,
} from './WorkyAuthQueryParamService';
import { requestCognitoPasswordReset } from './WorkyCognitoAuthService';
import type { CognitoPasswordResetFailureResult } from './WorkyCognitoAuthServiceInterfaces';
import { isUnverifiedContactPasswordResetFailure } from './WorkyPasswordResetFailureService';
import './WorkyLoginPage.css';

const WorkyForgotPasswordPage = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const initialEmailAddress: string = useMemo(
        (): string => readWorkyAuthEmailFromLocationSearch(location.search),
        [location.search]
    );
    const [emailAddress, setEmailAddress] = useState<string>(initialEmailAddress);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleSubmit = useCallback(
        async (submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> => {
            submitEvent.preventDefault();
            if (isSubmitting) {
                return;
            }

            const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
            if (!isValidString(normalizedEmailAddress)) {
                enqueueSnackbar('Please enter your email address.', { variant: 'warning' });
                return;
            }

            setIsSubmitting(true);
            try {
                const resetFailure: CognitoPasswordResetFailureResult | null =
                    await requestCognitoPasswordReset(normalizedEmailAddress);
                if (resetFailure !== null) {
                    if (isUnverifiedContactPasswordResetFailure(resetFailure)) {
                        enqueueSnackbar(
                            'Your email is not verified yet. We can send you a verification code.',
                            { variant: 'warning' }
                        );
                        history.push(
                            buildWorkyAuthPathWithEmail(
                                WORKY_WEB_APP_PATHS.verifyEmail,
                                normalizedEmailAddress
                            )
                        );
                        return;
                    }

                    enqueueSnackbar(resetFailure.errorMessage, { variant: 'error' });
                    return;
                }

                enqueueSnackbar('A password reset code was sent to your email.', {
                    variant: 'success',
                });
                history.push(
                    buildWorkyAuthPathWithEmail(
                        WORKY_WEB_APP_PATHS.resetPassword,
                        normalizedEmailAddress
                    )
                );
            } finally {
                setIsSubmitting(false);
            }
        },
        [emailAddress, enqueueSnackbar, history, isSubmitting]
    );

    const handleNavigateToLogin = useCallback((): void => {
        history.push(
            isValidString(emailAddress)
                ? buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.login, emailAddress)
                : WORKY_WEB_APP_PATHS.login
        );
    }, [emailAddress, history]);

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleNavigateToLogin}
            >
                <ArrowBackIcon fontSize="small" />
                Back to sign in
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateToLogin}
                    aria-label="Worky sign in"
                >
                    <img src={WORKY_BRAND_LOGO_SRC} alt="Worky" className="worky-login-logo-image" />
                </button>

                <h1 className="worky-login-title">Forgot password</h1>
                <p className="worky-login-subtitle">
                    Enter your account email and we’ll send you a password reset code.
                </p>

                <form className="worky-login-form" onSubmit={handleSubmit}>
                    <TextField
                        label="Email"
                        type="email"
                        autoComplete="email"
                        value={emailAddress}
                        onChange={(changeEvent): void => setEmailAddress(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="worky-login-submit-button"
                        disabled={isSubmitting}
                        fullWidth
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send reset code'}
                    </Button>
                </form>

                <div className="worky-login-footer">
                    Remembered your password?{' '}
                    <span
                        className="worky-login-footer-link"
                        onClick={handleNavigateToLogin}
                        role="button"
                        tabIndex={0}
                    >
                        Sign in
                    </span>
                </div>
            </div>
        </div>
    );
};

export { WorkyForgotPasswordPage };
